import React, { useState, useEffect, useCallback, useMemo } from "react";
import type { CanvasSettings, EditorElement } from "../../../editor/types";
import { renderCanvasElementContent } from "../lib/renderers";
import { resolveVideoStyle } from "../lib/appearance";
import { useLocale } from "../../../locales";
import {
  checkPreviewAnswers,
  type PreviewCheckResult,
} from "../lib/validation";

const PAINT_SWATCH_PREFIX = "paint-swatch:";
const PAINT_INTERSECTION_SWATCH_PREFIX = "paint-intersection-swatch:";
const DEFAULT_MEDIA_FALLBACK_MS = 8000;
const hasTimingSettings = (element: EditorElement) =>
  typeof element.appearOrder === "number" ||
  typeof element.appearDelayMs === "number" ||
  typeof element.minDisplayMs === "number";

const useVisibleElementIds = (elements: EditorElement[]) => {
  const scheduledReveals = React.useMemo(() => {
    let cumulative = 0;

    return elements
      .map((element, index) => ({ element, index }))
      .filter(({ element }) => hasTimingSettings(element))
      .sort((a, b) => {
        const aHasOrder = typeof a.element.appearOrder === "number";
        const bHasOrder = typeof b.element.appearOrder === "number";

        if (aHasOrder && bHasOrder) {
          return (a.element.appearOrder ?? 0) - (b.element.appearOrder ?? 0);
        }

        if (aHasOrder !== bHasOrder) {
          return aHasOrder ? -1 : 1;
        }

        return a.index - b.index;
      })
      .map(({ element: el }) => {
        cumulative += Math.max(0, el.appearDelayMs ?? 0);
        const showAt = cumulative;
        const isMedia = el.type === "slide" || el.type === "audio";
        cumulative += Math.max(
          0,
          el.minDisplayMs ?? (isMedia ? DEFAULT_MEDIA_FALLBACK_MS : 0)
        );
        return { elementId: el.id, showAt };
      });
  }, [elements]);

  const initiallyVisibleIds = React.useMemo(
    () =>
      elements
        .filter((el) => !hasTimingSettings(el))
        .map((el) => el.id),
    [elements]
  );

  const [visible, setVisible] = useState<Set<string>>(
    () => new Set(initiallyVisibleIds)
  );
  const elapsedMsRef = React.useRef(0);
  const lastTickAtRef = React.useRef<number | null>(null);

  useEffect(() => {
    elapsedMsRef.current = 0;
    lastTickAtRef.current = null;
    setVisible(new Set(initiallyVisibleIds));
  }, [initiallyVisibleIds, scheduledReveals]);

  useEffect(() => {
    if (scheduledReveals.length === 0) {
      lastTickAtRef.current = null;
      return;
    }

    let intervalId = 0;
    lastTickAtRef.current = window.performance.now();

    const revealElapsedItems = () => {
      const now = window.performance.now();
      const lastTickAt = lastTickAtRef.current ?? now;
      elapsedMsRef.current += now - lastTickAt;
      lastTickAtRef.current = now;

      setVisible((prev) => {
        let changed = false;
        const next = new Set(prev);
        for (const item of scheduledReveals) {
          if (elapsedMsRef.current >= item.showAt && !next.has(item.elementId)) {
            next.add(item.elementId);
            changed = true;
          }
        }
        return changed ? next : prev;
      });

      if (scheduledReveals.every((item) => elapsedMsRef.current >= item.showAt)) {
        window.clearInterval(intervalId);
      }
    };

    intervalId = window.setInterval(revealElapsedItems, 100);
    revealElapsedItems();

    return () => {
      window.clearInterval(intervalId);
      lastTickAtRef.current = null;
    };
  }, [scheduledReveals]);

  return visible;
};

interface FullScreenPreviewProps {
  elements: EditorElement[];
  canvas: CanvasSettings;
  onClose: () => void;
}

const FullScreenPreview: React.FC<FullScreenPreviewProps> = ({
  elements,
  canvas,
  onClose,
}) => {
  const { t } = useLocale();
  const visibleElementIds = useVisibleElementIds(elements);
  // Form state for drag-drop interactions
  const [formState, setFormState] = useState<
    Record<string, string | string[] | Record<string, string>>
  >({});
  const [matchingConnections, setMatchingConnections] = useState<
    Record<string, string[]>
  >({});
  const [checkResult, setCheckResult] = useState<PreviewCheckResult | null>(
    null
  );
  const [draggingSource, setDraggingSource] = useState<{
    sourceId: string;
    sourcePos: { x: number; y: number };
    mousePos: { x: number; y: number };
  } | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(
    null
  );
  const [snappedTarget, setSnappedTarget] = useState<string | null>(null); // Yakındaki target point ID
  const [hoveredConnection, setHoveredConnection] = useState<string | null>(
    null
  ); // Hover durumundaki connection key

  // Hidden elements state (for video show-elements-after-video feature)
  const [hiddenElementIds, setHiddenElementIds] = useState<Set<string>>(() => {
    // Initially hide elements that are in any slide's showElementsAfterVideo list
    const hiddenIds = new Set<string>();
    elements.forEach((el) => {
      if (el.type === "slide") {
        const videoStyle = resolveVideoStyle(el);
        const showElementsAfterVideo = videoStyle.showElementsAfterVideo || [];
        showElementsAfterVideo.forEach((id) => {
          hiddenIds.add(id);
        });
      }
    });
    return hiddenIds;
  });

  // Listen for show-elements-after-video event
  useEffect(() => {
    const handleShowElementsAfterVideo = (
      e: CustomEvent<{ elementIds: string[] }>
    ) => {
      setHiddenElementIds((prev) => {
        const next = new Set(prev);
        e.detail.elementIds.forEach((id) => {
          next.delete(id);
        });
        return next;
      });
    };

    window.addEventListener(
      "show-elements-after-video",
      handleShowElementsAfterVideo as EventListener
    );
    return () => {
      window.removeEventListener(
        "show-elements-after-video",
        handleShowElementsAfterVideo as EventListener
      );
    };
  }, []);

  // Update hidden elements when elements change
  useEffect(() => {
    const hiddenIds = new Set<string>();
    elements.forEach((el) => {
      if (el.type === "slide") {
        const videoStyle = resolveVideoStyle(el);
        const showElementsAfterVideo = videoStyle.showElementsAfterVideo || [];
        showElementsAfterVideo.forEach((id) => {
          hiddenIds.add(id);
        });
      }
    });
    setHiddenElementIds(hiddenIds);
  }, [elements]);

  // Handle choice changes
  const handleChoiceChange = useCallback(
    (elementId: string, optionId: string, isMultiple: boolean) => {
      setFormState((prev) => {
        const currentValue = prev[elementId];
        let newValue: string | string[];

        if (isMultiple) {
          const currentArray = Array.isArray(currentValue) ? currentValue : [];
          newValue = currentArray.includes(optionId)
            ? currentArray.filter((id) => id !== optionId)
            : [...currentArray, optionId];
        } else {
          newValue = optionId;
        }

        const newFormState = {
          ...prev,
          [elementId]: newValue,
        };

        console.log("FormState güncellendi:", newFormState);
        return newFormState;
      });
    },
    []
  );

  // Handle drag-drop changes for drop targets
  const handleDragDropChange = useCallback(
    (
      targetId: string,
      sourceIds: string | string[],
      sourceId: string | null
    ) => {
      setFormState((prev) => {
        const newFormState = {
          ...prev,
          [targetId]: sourceIds,
        };
        console.log("FormState güncellendi:", newFormState);
        return newFormState;
      });
    },
    []
  );

  const handlePushToTalkChange = useCallback(
    (elementId: string, transcript: string) => {
      setFormState((prev) => {
        const newFormState = {
          ...prev,
          [elementId]: transcript,
        };
        console.log("FormState güncellendi:", newFormState);
        return newFormState;
      });
    },
    []
  );

  // Handle fill-in-the-blanks changes (values: blankId -> text)
  const handleFillInTheBlanksChange = useCallback(
    (elementId: string, values: Record<string, string>) => {
      setFormState((prev) => ({
        ...prev,
        [elementId]: values,
      }));
    },
    []
  );

  // Validate the user's current answers against the answer key
  const handleCheckAnswers = useCallback(() => {
    const result = checkPreviewAnswers(
      elements,
      canvas.question?.answerKey,
      formState,
      matchingConnections
    );
    setCheckResult(result);
  }, [elements, canvas.question?.answerKey, formState, matchingConnections]);

  // Boyama anlık geri bildirimi için: paint-area id -> doğru renk (answer
  // key'deki paintColor'dan).
  const paintCorrectColors = useMemo(() => {
    const map: Record<string, string> = {};
    const ak = canvas.question?.answerKey;
    if (ak) {
      for (const [id, value] of Object.entries(ak)) {
        if (!value || typeof value !== "object" || Array.isArray(value)) {
          continue;
        }
        const cfg = value as {
          paintColor?: unknown;
          paintSwatchId?: unknown;
        };
        if (typeof cfg.paintColor === "string" && cfg.paintColor) {
          map[id] = cfg.paintColor;
        } else if (
          typeof cfg.paintSwatchId === "string" &&
          cfg.paintSwatchId
        ) {
          const swatch = elements.find((el) => el.id === cfg.paintSwatchId);
          const color = (
            swatch?.data as { appearance?: { color?: string } } | undefined
          )?.appearance?.color;
          if (typeof color === "string") {
            map[id] = color;
          }
        }
      }
    }
    return map;
  }, [canvas.question?.answerKey, elements]);

  // Küp çubuğu id -> beklenen toplam (answer key'den).
  const cubeExpectedTotals = useMemo(() => {
    const map: Record<string, number> = {};
    const ak = canvas.question?.answerKey;
    if (ak) {
      for (const [id, value] of Object.entries(ak)) {
        if (!value || typeof value !== "object" || Array.isArray(value)) {
          continue;
        }
        const total = (value as { expectedTotal?: unknown }).expectedTotal;
        if (typeof total === "number") {
          map[id] = total;
        }
      }
    }
    return map;
  }, [canvas.question?.answerKey]);

  // Clear a stale check result whenever the user changes an answer
  useEffect(() => {
    setCheckResult(null);
  }, [formState, matchingConnections]);

  const getCheckTypeLabel = useCallback(
    (elementType: string): string => {
      switch (elementType) {
        case "choice":
          return t("fullScreenPreview.typeChoice");
        case "drop-target":
          return t("fullScreenPreview.typeDragDrop");
        case "order-list":
          return t("fullScreenPreview.typeOrderList");
        case "matching-point":
          return t("fullScreenPreview.typeMatching");
        case "fill-in-the-blanks":
          return t("fullScreenPreview.typeFillBlanks");
        case "paint-area":
          return t("fullScreenPreview.typePaint");
        default:
          return elementType;
      }
    },
    [t]
  );

  // Map of text-element-id -> audio-element-id for audios that should auto-play
  // while the linked text element is hovered (set via "place behind text").
  const hoverAudioByTextId = React.useMemo(() => {
    const map: Record<string, string> = {};
    elements.forEach((el) => {
      if (el.type !== "audio") {
        return;
      }
      const data = el.data as {
        generatedFromTextElementId?: string;
        style?: { playOnHover?: boolean };
      };
      if (data?.style?.playOnHover && data.generatedFromTextElementId) {
        map[data.generatedFromTextElementId] = el.id;
      }
    });
    return map;
  }, [elements]);

  // Global drop zone handler
  const handleGlobalDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    const draggedItemId = e.dataTransfer?.getData(
      "application/x-dropped-item-id"
    );
    const draggedBaseId =
      e.dataTransfer?.getData("application/x-dropped-item-base-id") ||
      draggedItemId;
    const sourceDropTargetId = e.dataTransfer?.getData(
      "application/x-drop-target-id"
    );

    // If an item from any drop-target was dragged onto the fullscreen preview background (empty area), remove it
    if (draggedItemId && sourceDropTargetId) {
      setFormState((prev) => {
        const currentValues = prev[sourceDropTargetId];
        let newValues: string[] | string;

        if (Array.isArray(currentValues)) {
          const nextArr = [...currentValues];
          let removed = false;
          const instanceIndex = nextArr.indexOf(draggedItemId);
          if (instanceIndex !== -1) {
            nextArr.splice(instanceIndex, 1);
            removed = true;
          }
          if (!removed) {
            const baseIndex = nextArr.indexOf(draggedBaseId);
            if (baseIndex !== -1) {
              nextArr.splice(baseIndex, 1);
              removed = true;
            }
          }
          if (!removed) {
            return prev;
          }
          newValues = nextArr;
        } else if (
          typeof currentValues === "string" &&
          (currentValues === draggedItemId || currentValues === draggedBaseId)
        ) {
          newValues = [];
        } else {
          return prev;
        }

        const newState = {
          ...prev,
          [sourceDropTargetId]: newValues,
        };

        // Notify that the source element should be visible again
        window.dispatchEvent(
          new CustomEvent("drag-source-freed", {
            detail: { sourceId: draggedBaseId },
          })
        );

        return newState;
      });
    }
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      // Only allow drop if it's a dropped item being dragged out
      if (e.dataTransfer?.types.includes("application/x-dropped-item-id")) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      }
      // Allow matching point connections
      if (e.dataTransfer?.types.includes("application/x-matching-source-id")) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "link";

        // Mouse pozisyonunu güncelle (canvas'a göre)
        const canvasElement = e.currentTarget.querySelector(
          ".qe-fullscreen-preview__canvas"
        );
        if (canvasElement) {
          const rect = canvasElement.getBoundingClientRect();
          const mouseX = e.clientX - rect.left;
          const mouseY = e.clientY - rect.top;
          setMousePos({ x: mouseX, y: mouseY });

          // Mıknatıs efekti: Yakındaki target point'leri kontrol et
          const snapDistance = 50; // 50px mesafe içindeki target point'lere yapış
          const targetPoints = elements.filter(
            (el) =>
              el.type === "matching-point" &&
              (el.data as { pointType?: string })?.pointType === "target"
          );

          let closestTargetId: string | null = null;
          let closestDistance = Infinity;

          targetPoints.forEach((targetPoint) => {
            const targetCenterX =
              targetPoint.position.x + (targetPoint.size.width || 16) / 2;
            const targetCenterY =
              targetPoint.position.y + (targetPoint.size.height || 16) / 2;

            const dx = mouseX - targetCenterX;
            const dy = mouseY - targetCenterY;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance <= snapDistance && distance < closestDistance) {
              closestTargetId = targetPoint.id;
              closestDistance = distance;
            }
          });

          setSnappedTarget(closestTargetId);
        }
      }
    },
    [elements]
  );

  // Drag başladığında source point bilgisini al
  useEffect(() => {
    const handleMatchingDragStart = (e: CustomEvent) => {
      const { sourceId, sourcePos, mousePos } = e.detail;
      setDraggingSource({
        sourceId,
        sourcePos,
        mousePos: mousePos || sourcePos,
      });
    };

    window.addEventListener(
      "matching-drag-start",
      handleMatchingDragStart as EventListener
    );
    return () => {
      window.removeEventListener(
        "matching-drag-start",
        handleMatchingDragStart as EventListener
      );
    };
  }, []);

  // Initialize matchingConnections as empty - preview should start fresh like a student's view
  // Don't load connections from element.data.connections (which may contain answer key data)
  // Students should make their own connections, not see pre-filled answers
  useEffect(() => {
    // Always start with empty connections in preview mode
    // This ensures preview behaves like a student's view, not showing answer keys
    setMatchingConnections({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements]);

  // Handle matching point updates
  const handleMatchingUpdate = useCallback(
    (elementId: string, changes: Partial<any>) => {
      // onUpdate callback'i Partial<EditorElement> alır, bu yüzden data.data.connections'ı kontrol et
      const connections =
        (changes.data as any)?.connections ?? changes.connections;
      if (connections !== undefined) {
        setMatchingConnections((prev) => ({
          ...prev,
          [elementId]: Array.isArray(connections) ? connections : [],
        }));
      }
    },
    []
  );

  const handlePaintAreaUpdate = useCallback(
    (elementId: string, changes: Partial<any>) => {
      const paintedColor = (changes as { paintedColor?: string }).paintedColor;
      const paintSwatchId = (changes as { paintSwatchId?: string | null })
        .paintSwatchId;
      const intersectionKey = (changes as { intersectionKey?: string })
        .intersectionKey;
      const intersectionColor = (changes as { intersectionColor?: string })
        .intersectionColor;
      const intersectionSwatchId = (
        changes as { intersectionSwatchId?: string | null }
      ).intersectionSwatchId;
      setFormState((prev) => {
        const next = { ...prev };
        if (paintedColor !== undefined) {
          next[elementId] = paintedColor;
        }
        if (paintSwatchId !== undefined) {
          const key = `${PAINT_SWATCH_PREFIX}${elementId}`;
          if (paintSwatchId) {
            next[key] = paintSwatchId;
          } else {
            delete next[key];
          }
        }
        if (intersectionKey && intersectionColor) {
          next[`paint-intersection:${intersectionKey}`] = intersectionColor;
        }
        if (intersectionKey && intersectionSwatchId !== undefined) {
          const key = `${PAINT_INTERSECTION_SWATCH_PREFIX}${intersectionKey}`;
          if (intersectionSwatchId) {
            next[key] = intersectionSwatchId;
          } else {
            delete next[key];
          }
        }
        return next;
      });
    },
    []
  );

  const handleElementUpdate = useCallback(
    (elementId: string, changes: Partial<any>) => {
      const element = elements.find((el) => el.id === elementId);
      if (element?.type === "matching-point") {
        handleMatchingUpdate(elementId, changes);
        return;
      }
      if (element?.type === "paint-area") {
        handlePaintAreaUpdate(elementId, changes);
      }
    },
    [elements, handleMatchingUpdate, handlePaintAreaUpdate]
  );

  // Drag bittiğinde bağlantıyı kur
  useEffect(() => {
    const handleDragEnd = () => {
      // Eğer mıknatıs efekti ile bir target'a yaklaşıldıysa, bağlantıyı otomatik kur
      if (draggingSource && snappedTarget) {
        const sourcePoint = elements.find(
          (el) => el.id === draggingSource.sourceId
        );
        if (sourcePoint && sourcePoint.type === "matching-point") {
          const sourceData = sourcePoint.data as {
            pointType?: "source" | "target";
            connections?: string[];
            allowMultipleConnections?: boolean;
          };
          if (sourceData.pointType === "source") {
            // Preview modunda matchingConnections state'ini kullan (cevap anahtarına bakmadan)
            const currentConnections =
              matchingConnections[draggingSource.sourceId] || [];
            const allowMultiple = sourceData.allowMultipleConnections || false;

            if (!currentConnections.includes(snappedTarget)) {
              // Eğer birden fazla bağlantıya izin verilmiyorsa, mevcut bağlantıları temizle
              const updatedConnections = allowMultiple
                ? [...currentConnections, snappedTarget]
                : [snappedTarget];

              handleMatchingUpdate(draggingSource.sourceId, {
                connections: updatedConnections,
              });
            }
          }
        }
      }

      setDraggingSource(null);
      setMousePos(null);
      setSnappedTarget(null);
    };

    window.addEventListener("dragend", handleDragEnd);
    return () => {
      window.removeEventListener("dragend", handleDragEnd);
    };
  }, [
    draggingSource,
    snappedTarget,
    elements,
    handleMatchingUpdate,
    matchingConnections,
  ]);

  // Handle Escape key to close preview
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Disable body scroll when preview is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <div
      className="qe-fullscreen-preview"
      onClick={onClose}
      onDrop={handleGlobalDrop}
      onDragOver={handleDragOver}
    >
      <div
        className="qe-fullscreen-preview__content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="qe-fullscreen-preview__header">
          <div className="qe-fullscreen-preview__title">{t("fullScreenPreview.title")}</div>
          <div className="qe-fullscreen-preview__header-actions">
            <button
              className="qe-fullscreen-preview__check"
              type="button"
              onClick={handleCheckAnswers}
            >
              {t("fullScreenPreview.checkAnswer")}
            </button>
            <button
              className="qe-fullscreen-preview__close"
              type="button"
              onClick={onClose}
              aria-label={t("fullScreenPreview.close")}
            >
              {t("fullScreenPreview.close")}
            </button>
          </div>
        </div>
        {checkResult && (
          <div
            className={`qe-preview-check ${
              !checkResult.hasGradable
                ? "qe-preview-check--info"
                : checkResult.allCorrect
                ? "qe-preview-check--success"
                : "qe-preview-check--warn"
            }`}
            role="status"
          >
            <button
              className="qe-preview-check__dismiss"
              type="button"
              onClick={() => setCheckResult(null)}
              aria-label={t("fullScreenPreview.close")}
            >
              ×
            </button>
            <div className="qe-preview-check__summary">
              {!checkResult.hasGradable
                ? t("fullScreenPreview.nothingToCheck")
                : checkResult.allCorrect
                ? t("fullScreenPreview.allCorrect")
                : t("fullScreenPreview.summary")
                    .replace("{correct}", String(checkResult.correct))
                    .replace("{total}", String(checkResult.total))}
            </div>
            {checkResult.hasGradable && (
              <ul className="qe-preview-check__list">
                {checkResult.items.map((item, index) => (
                  <li
                    key={item.elementId}
                    className={`qe-preview-check__item ${
                      item.isCorrect
                        ? "is-correct"
                        : item.answered
                        ? "is-wrong"
                        : "is-empty"
                    }`}
                  >
                    <span className="qe-preview-check__item-label">
                      {index + 1}. {getCheckTypeLabel(item.elementType)}
                    </span>
                    <span className="qe-preview-check__item-status">
                      {item.isCorrect
                        ? t("fullScreenPreview.resultCorrect")
                        : item.answered
                        ? t("fullScreenPreview.resultWrong")
                        : t("fullScreenPreview.resultEmpty")}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div className="qe-fullscreen-preview__canvas-wrapper">
          <div
            className="qe-fullscreen-preview__canvas"
            style={{
              width: canvas.width,
              height: canvas.height,
              backgroundColor: canvas.background,
              position: "relative",
              overflow: "hidden",
              maxWidth: "100%",
            }}
            onDragOver={handleDragOver}
          >
            {/* Matching connections SVG overlay */}
            {(() => {
              const matchingPoints = elements.filter(
                (el) => el.type === "matching-point"
              );
              const connections: Array<{
                sourceId: string;
                targetId: string;
                sourcePos: { x: number; y: number };
                targetPos: { x: number; y: number };
              }> = [];

              matchingPoints.forEach((sourcePoint) => {
                const sourceData = sourcePoint.data as {
                  pointType?: "source" | "target";
                  connections?: string[];
                };
                // In preview mode, only use matchingConnections state (user's interactions)
                // Don't fall back to element.data.connections which may contain answer key data
                // Preview should behave like a student's view - start fresh
                const connectionsData =
                  matchingConnections[sourcePoint.id] || [];
                if (
                  sourceData.pointType === "source" &&
                  connectionsData.length > 0
                ) {
                  connectionsData.forEach((targetId) => {
                    const targetPoint = elements.find(
                      (el) => el.id === targetId
                    );
                    if (targetPoint) {
                      const sourceCenterX =
                        sourcePoint.position.x +
                        (sourcePoint.size.width || 16) / 2;
                      const sourceCenterY =
                        sourcePoint.position.y +
                        (sourcePoint.size.height || 16) / 2;
                      const targetCenterX =
                        targetPoint.position.x +
                        (targetPoint.size.width || 16) / 2;
                      const targetCenterY =
                        targetPoint.position.y +
                        (targetPoint.size.height || 16) / 2;
                      connections.push({
                        sourceId: sourcePoint.id,
                        targetId,
                        sourcePos: { x: sourceCenterX, y: sourceCenterY },
                        targetPos: { x: targetCenterX, y: targetCenterY },
                      });
                    }
                  });
                }
              });

              // Drag sırasında geçici çizgi ekle
              if (draggingSource && mousePos) {
                // Source point'in merkezinden başla
                const sourcePoint = elements.find(
                  (el) => el.id === draggingSource.sourceId
                );
                const sourcePos = sourcePoint
                  ? {
                      x:
                        sourcePoint.position.x +
                        (sourcePoint.size.width || 16) / 2,
                      y:
                        sourcePoint.position.y +
                        (sourcePoint.size.height || 16) / 2,
                    }
                  : draggingSource.sourcePos;

                // Eğer yakında bir target point varsa, ona yapış
                let targetPos = mousePos;
                if (snappedTarget) {
                  const targetPoint = elements.find(
                    (el) => el.id === snappedTarget
                  );
                  if (targetPoint) {
                    targetPos = {
                      x:
                        targetPoint.position.x +
                        (targetPoint.size.width || 16) / 2,
                      y:
                        targetPoint.position.y +
                        (targetPoint.size.height || 16) / 2,
                    };
                  }
                }

                connections.push({
                  sourceId: draggingSource.sourceId,
                  targetId: snappedTarget || "temp",
                  // Çizgi source point'in merkezinden başlar, mouse pozisyonuna veya target point'e gider
                  sourcePos,
                  targetPos,
                });
              }

              if (connections.length === 0) return null;

              // Helper function to get appearance from source point
              const getSourceAppearance = (sourceId: string) => {
                const sourcePoint = matchingPoints.find(
                  (p) => p.id === sourceId
                );
                if (sourcePoint) {
                  const data = sourcePoint.data as { appearance?: any };
                  // connectionWidth string olarak saklanmış olabilir, number'a çevir
                  let connectionWidth = data.appearance?.connectionWidth;
                  if (typeof connectionWidth === "string") {
                    connectionWidth = parseFloat(connectionWidth) || 2;
                  } else if (typeof connectionWidth === "number") {
                    connectionWidth = connectionWidth || 2;
                  } else {
                    connectionWidth = 2;
                  }
                  return {
                    connectionColor:
                      data.appearance?.connectionColor || "#2563eb",
                    connectionWidth,
                    connectionStyle:
                      data.appearance?.connectionStyle || "solid",
                  };
                }
                return {
                  connectionColor: "#2563eb",
                  connectionWidth: 2,
                  connectionStyle: "solid",
                };
              };

              return (
                <svg
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none",
                    zIndex: 100, // Point'lerin altında (150) ama diğer elementlerin üstünde
                    overflow: "visible",
                  }}
                >
                  <defs></defs>
                  {connections.map((conn, index) => {
                    // Her connection için kendi source point'inin appearance'ını kullan
                    const appearance = getSourceAppearance(conn.sourceId);

                    const isTemporary =
                      conn.targetId === "temp" ||
                      (snappedTarget && conn.targetId === snappedTarget);

                    // Geçici çizgi için (hem temp hem de snapped target için)
                    if (isTemporary) {
                      // Source point'in border'ının dışından başla (kalıcı çizgiler gibi)
                      const sourcePoint = matchingPoints.find(
                        (p) => p.id === conn.sourceId
                      );

                      // Point'in görsel yarıçapını al (appearance.pointSize kullan)
                      const getPointRadius = (point: typeof sourcePoint) => {
                        if (!point) return 8;
                        const data = point.data as {
                          appearance?: { pointSize?: number | string };
                        };
                        const pointSize = data.appearance?.pointSize;
                        if (typeof pointSize === "number") {
                          return pointSize / 2;
                        } else if (typeof pointSize === "string") {
                          return parseFloat(pointSize) / 2 || 8;
                        }
                        // Fallback: size.width kullan (eski format için)
                        return (point.size.width || 16) / 2;
                      };

                      const sourceRadius = getPointRadius(sourcePoint);

                      const getBorderWidth = (point: typeof sourcePoint) => {
                        if (!point) return 0;
                        const data = point.data as {
                          appearance?: { borderWidth?: number | string };
                        };
                        const borderWidth = data.appearance?.borderWidth;
                        if (typeof borderWidth === "number") {
                          return borderWidth;
                        } else if (typeof borderWidth === "string") {
                          return parseFloat(borderWidth) || 0;
                        }
                        return 0;
                      };

                      const sourceBorderWidth = getBorderWidth(sourcePoint);

                      // Source'dan target'a doğru birim vektörü hesapla
                      const dx = conn.targetPos.x - conn.sourcePos.x;
                      const dy = conn.targetPos.y - conn.sourcePos.y;
                      const distance = Math.sqrt(dx * dx + dy * dy);
                      if (distance === 0) return null;
                      const unitX = dx / distance;
                      const unitY = dy / distance;

                      // Source point'in border'ının dışından başla
                      const startX =
                        conn.sourcePos.x +
                        unitX * (sourceRadius + sourceBorderWidth);
                      const startY =
                        conn.sourcePos.y +
                        unitY * (sourceRadius + sourceBorderWidth);

                      // Eğer snapped target varsa, onun kenarına kadar çiz
                      let endX = conn.targetPos.x;
                      let endY = conn.targetPos.y;
                      if (
                        conn.targetId !== "temp" &&
                        snappedTarget === conn.targetId
                      ) {
                        const targetPoint = matchingPoints.find(
                          (p) => p.id === conn.targetId
                        );
                        if (targetPoint) {
                          // Point'in görsel yarıçapını al (appearance.pointSize kullan)
                          const getPointRadius = (
                            point: typeof targetPoint
                          ) => {
                            if (!point) return 8;
                            const data = point.data as {
                              appearance?: { pointSize?: number | string };
                            };
                            const pointSize = data.appearance?.pointSize;
                            if (typeof pointSize === "number") {
                              return pointSize / 2;
                            } else if (typeof pointSize === "string") {
                              return parseFloat(pointSize) / 2 || 8;
                            }
                            // Fallback: size.width kullan (eski format için)
                            return (point.size.width || 16) / 2;
                          };

                          const targetRadius = getPointRadius(targetPoint);

                          const getBorderWidth = (
                            point: typeof targetPoint
                          ) => {
                            if (!point) return 0;
                            const data = point.data as {
                              appearance?: { borderWidth?: number | string };
                            };
                            const borderWidth = data.appearance?.borderWidth;
                            if (typeof borderWidth === "number") {
                              return borderWidth;
                            } else if (typeof borderWidth === "string") {
                              return parseFloat(borderWidth) || 0;
                            }
                            return 0;
                          };

                          const targetBorderWidth = getBorderWidth(targetPoint);

                          // Target point'in border'ının dışından bitir
                          endX =
                            conn.targetPos.x -
                            unitX * (targetRadius + targetBorderWidth);
                          endY =
                            conn.targetPos.y -
                            unitY * (targetRadius + targetBorderWidth);
                        }
                      }

                      const curveDx = endX - startX;
                      const controlX1 = startX + curveDx * 0.5;
                      const controlY1 = startY;
                      const controlX2 = endX - curveDx * 0.5;
                      const controlY2 = endY;

                      // Geçici çizgi için de connectionStyle uygula
                      // Kalınlığa göre dash array'i ayarla
                      const width = appearance.connectionWidth || 2;
                      const dashArray =
                        appearance.connectionStyle === "dashed"
                          ? `${width * 4}, ${width * 2}`
                          : appearance.connectionStyle === "dotted"
                          ? `${width * 0.5}, ${width * 2}`
                          : "none";

                      return (
                        <g key={`temp-${conn.sourceId}-${index}`}>
                          <path
                            d={`M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`}
                            fill="none"
                            stroke={appearance.connectionColor}
                            strokeWidth={appearance.connectionWidth}
                            strokeLinecap="round"
                            strokeDasharray={dashArray}
                            style={{
                              pointerEvents: "none",
                            }}
                            opacity={0.5}
                          />
                        </g>
                      );
                    }

                    // Kalıcı çizgiler için - çizgi point'lerin border'ının dış kenarından başlar ve biter
                    const sourcePoint = matchingPoints.find(
                      (p) => p.id === conn.sourceId
                    );
                    const targetPoint = matchingPoints.find(
                      (p) => p.id === conn.targetId
                    );

                    // Point'in görsel yarıçapını al (appearance.pointSize kullan)
                    const getPointRadius = (point: typeof sourcePoint) => {
                      if (!point) return 8;
                      const data = point.data as {
                        appearance?: { pointSize?: number | string };
                      };
                      const pointSize = data.appearance?.pointSize;
                      if (typeof pointSize === "number") {
                        return pointSize / 2;
                      } else if (typeof pointSize === "string") {
                        return parseFloat(pointSize) / 2 || 8;
                      }
                      // Fallback: size.width kullan (eski format için)
                      return (point.size.width || 16) / 2;
                    };

                    const sourceRadius = getPointRadius(sourcePoint);
                    const targetRadius = getPointRadius(targetPoint);

                    // Border width'leri al (çizgi border'ın dışından başlamalı)
                    const getBorderWidth = (point: typeof sourcePoint) => {
                      if (!point) return 0;
                      const data = point.data as {
                        appearance?: { borderWidth?: number | string };
                      };
                      const borderWidth = data.appearance?.borderWidth;
                      if (typeof borderWidth === "number") {
                        return borderWidth;
                      } else if (typeof borderWidth === "string") {
                        return parseFloat(borderWidth) || 0;
                      }
                      return 0;
                    };

                    const sourceBorderWidth = getBorderWidth(sourcePoint);
                    const targetBorderWidth = getBorderWidth(targetPoint);

                    // Source'dan target'a doğru birim vektörü hesapla
                    const dx = conn.targetPos.x - conn.sourcePos.x;
                    const dy = conn.targetPos.y - conn.sourcePos.y;
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    if (distance === 0) return null; // Aynı noktada ise çizgi çizme
                    const unitX = dx / distance;
                    const unitY = dy / distance;

                    // Source point'in border'ının dışından başla (radius + borderWidth)
                    // box-sizing: border-box olduğu için border içeride, ama görsel olarak dış kenar = radius + borderWidth
                    const startX =
                      conn.sourcePos.x +
                      unitX * (sourceRadius + sourceBorderWidth);
                    const startY =
                      conn.sourcePos.y +
                      unitY * (sourceRadius + sourceBorderWidth);

                    // Target point'in border'ının dışından bitir
                    const endX =
                      conn.targetPos.x -
                      unitX * (targetRadius + targetBorderWidth);
                    const endY =
                      conn.targetPos.y -
                      unitY * (targetRadius + targetBorderWidth);

                    // Smooth curve için path kullan
                    const curveDx = endX - startX;
                    const controlX1 = startX + curveDx * 0.5;
                    const controlY1 = startY;
                    const controlX2 = endX - curveDx * 0.5;
                    const controlY2 = endY;

                    // connectionStyle'a göre stroke-dasharray değerini belirle
                    // connectionStyle'a göre stroke-dasharray değerini belirle
                    // Kalınlığa göre dash array'i ayarla
                    const width = appearance.connectionWidth || 2;
                    const dashArray =
                      appearance.connectionStyle === "dashed"
                        ? `${width * 4}, ${width * 2}`
                        : appearance.connectionStyle === "dotted"
                        ? `${width * 0.5}, ${width * 2}`
                        : "none"; // solid için

                    // Bezier curve üzerinde t=0.5 noktasını hesapla (çizginin ortası)
                    // B(t) = (1-t)³P₀ + 3(1-t)²tP₁ + 3(1-t)t²P₂ + t³P₃
                    // t=0.5 için: B(0.5) = 0.125*P₀ + 0.375*P₁ + 0.375*P₂ + 0.125*P₃
                    const t = 0.5;
                    const oneMinusT = 1 - t;
                    const midX =
                      oneMinusT * oneMinusT * oneMinusT * startX +
                      3 * oneMinusT * oneMinusT * t * controlX1 +
                      3 * oneMinusT * t * t * controlX2 +
                      t * t * t * endX;
                    const midY =
                      oneMinusT * oneMinusT * oneMinusT * startY +
                      3 * oneMinusT * oneMinusT * t * controlY1 +
                      3 * oneMinusT * t * t * controlY2 +
                      t * t * t * endY;

                    // Bağlantıyı kaldırma fonksiyonu
                    const handleRemoveConnection = (e: React.MouseEvent) => {
                      e.stopPropagation();
                      const sourceElement = elements.find(
                        (el) => el.id === conn.sourceId
                      );
                      if (!sourceElement) return;

                      const sourceData = sourceElement.data as {
                        pointType?: "source" | "target";
                        connections?: string[];
                        allowMultipleConnections?: boolean;
                      };

                      // In preview mode, only use matchingConnections state
                      // Don't fall back to sourceData.connections (answer key data)
                      const currentConnections =
                        matchingConnections[conn.sourceId] || [];
                      const updatedConnections = currentConnections.filter(
                        (id) => id !== conn.targetId
                      );

                      handleMatchingUpdate(conn.sourceId, {
                        data: {
                          ...sourceData,
                          connections: updatedConnections,
                        },
                      });
                    };

                    const connectionKey = `${conn.sourceId}-${conn.targetId}`;
                    const isHovered = hoveredConnection === connectionKey;

                    return (
                      <g key={`${conn.sourceId}-${conn.targetId}-${index}`}>
                        {/* Clickable path - clicking anywhere on the line removes the connection */}
                        <path
                          d={`M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`}
                          fill="none"
                          stroke={appearance.connectionColor}
                          strokeWidth={Math.max(appearance.connectionWidth + 8, 10)}
                          strokeLinecap="round"
                          strokeDasharray={dashArray}
                          onClick={handleRemoveConnection}
                          onMouseEnter={() => setHoveredConnection(connectionKey)}
                          onMouseLeave={() => setHoveredConnection(null)}
                          style={{
                            cursor: "pointer",
                            pointerEvents: "auto",
                            opacity: 0,
                          }}
                        />
                        {/* Visible path */}
                        <path
                          d={`M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`}
                          fill="none"
                          stroke={appearance.connectionColor}
                          strokeWidth={appearance.connectionWidth}
                          strokeLinecap="round"
                          strokeDasharray={dashArray}
                          style={{
                            filter: `drop-shadow(0 0 2px ${appearance.connectionColor}40)`,
                            pointerEvents: "none",
                          }}
                          opacity={isHovered ? 0.9 : 0.7}
                        />
                        {/* Gradient overlay for depth */}
                        <path
                          d={`M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`}
                          fill="none"
                          stroke={appearance.connectionColor}
                          strokeWidth={appearance.connectionWidth * 0.5}
                          strokeLinecap="round"
                          strokeDasharray={dashArray}
                          style={{
                            filter: `drop-shadow(0 0 2px ${appearance.connectionColor}40)`,
                            pointerEvents: "none",
                          }}
                          opacity={0.3}
                        />
                      </g>
                    );
                  })}
                </svg>
              );
            })()}
            {elements
              .filter((element) => !hiddenElementIds.has(element.id))
              .map((element) => {
                const isVisible = visibleElementIds.has(element.id);
                return (
                <div
                  key={element.id}
                  onMouseEnter={() => {
                    const audioId = hoverAudioByTextId[element.id];
                    if (audioId) {
                      window.dispatchEvent(
                        new CustomEvent("qe-audio-hover-play", {
                          detail: { elementId: audioId },
                        })
                      );
                    }
                  }}
                  onMouseLeave={() => {
                    const audioId = hoverAudioByTextId[element.id];
                    if (audioId) {
                      window.dispatchEvent(
                        new CustomEvent("qe-audio-hover-stop", {
                          detail: { elementId: audioId },
                        })
                      );
                    }
                  }}
                  style={{
                    position: "absolute",
                    top: element.position.y,
                    left: element.position.x,
                    width: element.size.width,
                    height: element.size.height,
                    transform: element.rotation
                      ? `rotate(${element.rotation}deg)`
                      : undefined,
                    // Preview modunda matching point'ler modal içinde olduğu için daha düşük z-index (modal z-index: 200)
                    zIndex:
                      element.type === "matching-point" ? 150 : element.zIndex,
                    opacity: isVisible ? 1 : 0,
                    visibility: isVisible ? "visible" : "hidden",
                    pointerEvents: isVisible ? "auto" : "none",
                    transition: "opacity 0.3s ease",
                  }}
                >
                  {renderCanvasElementContent(element, {
                    mode: "preview",
                    formState,
                    allElements: elements,
                    onDragDropChange: handleDragDropChange,
                    onChoiceChange: handleChoiceChange,
                    onPushToTalkChange: handlePushToTalkChange,
                    onFillInTheBlanksChange: handleFillInTheBlanksChange,
                    onUpdate: handleElementUpdate,
                    matchingConnections: matchingConnections,
                    paintCorrectColors,
                    cubeExpectedTotals,
                    onCubeBarChange: (elementId, total) =>
                      setFormState((prev) =>
                        prev[elementId] === String(total)
                          ? prev
                          : { ...prev, [elementId]: String(total) }
                      ),
                  })}
                </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullScreenPreview;
